import type { CityConfig } from "@/pages/city-template/types";

export const wolfeboroConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates — NH Dept of State Vital Records", href: "https://sos.nh.gov/vital-records/", description: "Official New Hampshire vital records office in Concord" },
    { label: "Marriage Licenses — Carroll County Clerk", href: "https://www.carrollcountynh.net/", description: "Carroll County Clerk — marriage license issuance" },
    { label: "Divorce Records — Carroll County Superior Court", href: "https://www.courts.nh.gov/", description: "Carroll County Superior Court — divorce decree records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of NH SOS for online vital record orders" },
  ],
  city: "Wolfeboro",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "wolfeboro",
  route: "/cities/wolfeboro",
  title: "Emergency & Expedited Passport Services Wolfeboro NH | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Wolfeboro, NH — as fast as 24 hours. No Wolfeboro passport agency. Fast Passport Center couriers to the Boston Passport Agency at Tip O'Neill Federal Building, about 120 miles south. A+ BBB rated. Serving Lake Winnipesaukee seasonal residents, Brewster Academy families, and all Lakes Region travelers heading abroad for study, business, or vacation.",
  h1: "Emergency & Expedited Passport Services in Wolfeboro, NH",
  heroSubtitle: "Wolfeboro sits on the eastern shore of Lake Winnipesaukee in Carroll County — known as 'The Oldest Summer Resort in America' and home to Brewster Academy. Fast Passport Center's registered couriers deliver your documents to the Boston Passport Agency at the Tip O'Neill Federal Building so you get a new or renewed passport as fast as 24 hours — whether you're a seasonal lakefront resident who discovered an expired passport before a European trip, a Brewster Academy family preparing for international travel, or a Lakes Region local heading abroad for the first time in years.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Wolfeboro%20New%20Hampshire%20downtown%20with%20Lake%20Winnipesaukee%20waterfront%20views%20and%20charming%20Main%20Street%20shops%20during%20warm%20summer%20afternoon%2C%20classic%20New%20England%20lakeside%20town%20with%20wooden%20docks%20and%20mountain%20backdrop%2C%20wide%20angle%20professional%20photography%20with%20clean%20composition%20and%20natural%20lake%20region%20colors&width=1800&height=700&seq=wolfeboro-nh-hero-001&orientation=landscape",
  heroImageAlt: "Wolfeboro New Hampshire downtown with Lake Winnipesaukee waterfront and mountain views",
  geoPosition: "43.5839;-71.2098",
  geoPlacename: "Wolfeboro, New Hampshire",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "Tip O'Neill Federal Building, 10 Causeway Street, Boston, MA 02222",
    distance: "Approximately 120 miles from Wolfeboro — about 2 hours 15 minutes via NH-28 and I-93",
  },
  nearbyCitiesZips: [
    { name: "Wolfeboro", zips: "03894" },
    { name: "Alton", zips: "03809" },
    { name: "Tuftonboro", zips: "03816" },
    { name: "Ossipee", zips: "03864" },
    { name: "Moultonborough", zips: "03254" },
    { name: "Wakefield", zips: "03872" },
    { name: "New Durham", zips: "03855" },
    { name: "Brookfield", zips: "03872" },
    { name: "Center Harbor", zips: "03226" },
    { name: "Meredith", zips: "03253" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Boston Passport Agency — about 120 miles from Wolfeboro via NH-28 and I-93" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Lakes Region Service", desc: "Serving Wolfeboro, Alton, Tuftonboro, Moultonborough, and all Lake Winnipesaukee towns" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Wolfeboro case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in New Hampshire", route: "/states/new-hampshire" },
    { name: "Expedited passport in Manchester NH", route: "/manchester" },
    { name: "Urgent passport in Wolfeboro NH", route: "/cities/wolfeboro" },
    { name: "New Hampshire passport hub", route: "/states/new-hampshire" },
    { name: "Fast passport in Concord NH", route: "/concord-nh" },
    { name: "Expedited passport in Laconia NH", route: "/cities/laconia" },
    { name: "Emergency passport in Conway NH", route: "/cities/conway-nh" },
  ],
  fpcOfficeAddress: "Tip O'Neill Federal Building, 10 Causeway Street, Boston, MA 02222",
  fpcOfficeNote: "Fast Passport Center serves Wolfeboro remotely via secure courier. Our registered couriers hand-deliver your documents to the Boston Passport Agency at the Tip O'Neill Federal Building — approximately 120 miles south. Wolfeboro's location in the Lakes Region means Boston courier routing is reliable via the NH-28 to I-93 corridor. Our dedicated agents ensure Lakes Region travelers never need to navigate Boston traffic for passport processing.",
  fpcOfficeDistance: "Serving all of Carroll County and the Lake Winnipesaukee region",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Wolfeboro, Alton, Tuftonboro, Moultonborough, Ossipee, and the Lakes Region corridor. From Brewster Academy families to seasonal lakefront residents, we help Wolfeboro travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Wolfeboro and nearby communities including Alton, Tuftonboro, Ossipee, Wakefield, Moultonborough, and the entire eastern Lake Winnipesaukee area.",
  howItWorksIntro: "Most Wolfeboro travelers complete everything online and ship documents overnight. At approximately 120 miles from Boston, Fast Passport Center provides fast courier turnaround for Lakes Region residents — eliminating the 2+ hour drive through New Hampshire and into Boston.",
  mistakesIntro: "Wolfeboro travelers, including seasonal lakefront residents and Brewster Academy families, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Wolfeboro and Lakes Region travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Wolfeboro? Here are the most common ones we hear from Lakes Region and Lake Winnipesaukee area travelers.",
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  dmvWebsite: "https://www.nh.gov/safety/divisions/dmv/index.htm",
  dmvLabel: "New Hampshire DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins at Causeway Street",
        "Life-or-death emergencies only",
        "120 miles to Boston (2 hours 15 minutes via NH-28/I-93)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for Brewster Academy families",
        "Perfect for seasonal lakefront residents",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from BOS or MHT",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Wolfeboro is 120 miles from the Boston Passport Agency — our courier service saves Lakes Region travelers the I-93 drive and Boston traffic.",
      bullets: ["Carroll County coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in the Lakes Region",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Wolfeboro adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Wolfeboro and Lakes Region"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Wolfeboro and Carroll County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Wolfeboro and Lakes Region residents with DS-64 form guidance and courier routing to Boston.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Boston agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Carroll County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Wolfeboro and Lakes Region residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Wolfeboro NH", description: "Urgent passport processing for Wolfeboro, NH travelers via Boston Passport Agency courier — 120 miles from Wolfeboro." },
    { name: "Passport Renewal Wolfeboro NH", description: "Fast passport renewal for Wolfeboro and Carroll County, New Hampshire residents." },
    { name: "New Passport Wolfeboro NH", description: "First-time passport application assistance for Wolfeboro and Lakes Region area residents." },
    { name: "Lost Passport Wolfeboro NH", description: "Passport replacement for lost or stolen passports for Wolfeboro and Carroll County residents." },
    { name: "Child Passport Wolfeboro NH", description: "Minor passport services for Wolfeboro and Lakes Region families." },
    { name: "Name Change Passport Wolfeboro NH", description: "Passport name change service for Wolfeboro, NH residents." },
  ],
  testimonials: [
    {
      name: "Katherine M.",
      location: "Wolfeboro Seasonal Resident",
      rating: 5,
      text: "We spend summers on Lake Winnipesaukee and winters in Florida. Realized 10 days before a Mediterranean cruise our passports had expired. Fast Passport Center renewed both in 5 business days. They handled everything while we were still at the lake — no trip to Boston needed.",
      service: "Passport Renewal",
    },
    {
      name: "The R Family",
      location: "Brewster Academy, Wolfeboro",
      rating: 5,
      text: "Our daughter attends Brewster Academy and was selected for a summer service trip to Costa Rica. We needed a first-time child passport with only 4 weeks before departure. Fast Passport Center processed everything through the Wolfeboro Post Office and couriered it to Boston — passport arrived in 7 business days.",
      service: "Child Passport",
    },
    {
      name: "David L.",
      location: "Tuftonboro, NH",
      rating: 5,
      text: "Retired physician who owns a home on Lake Winnipesaukee. My passport was stolen from my car during a trip to Boston. Fast Passport Center helped with the DS-64 and DS-11 forms simultaneously — new passport in 4 business days. Didn't miss my medical mission trip to Guatemala.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Nancy and Tom B.",
      location: "Alton, NH",
      rating: 5,
      text: "Lakes Region couple planning our 40th anniversary trip to Scotland. Discovered both passports expired 3 weeks before departure. Fast Passport Center renewed both simultaneously — had them in 6 business days. The dedicated agent was incredible — daily updates and zero stress.",
      service: "Dual Renewal",
    },
  ],
  facilities: [
    {
      id: "wlf-1",
      name: "Wolfeboro Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "24 Central Ave",
      city: "Wolfeboro",
      state: "NH",
      zip: "03894",
      phone: "(603) 569-3131",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Wolfeboro — Downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Wolfeboro and eastern Lakes Region residents. Appointment required. Located on Central Avenue near downtown.",
      icon: "ri-mail-line",
    },
    {
      id: "wlf-2",
      name: "Carroll County Clerk Office",
      type: "County Clerk Passport Acceptance",
      address: "95 Water Village Rd",
      city: "Ossipee",
      state: "NH",
      zip: "03864",
      phone: "(603) 539-2072",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Ossipee — 10 miles east of Wolfeboro",
      website: "https://www.carrollcountynh.net",
      websiteLabel: "Carroll County Government",
      notes: "County clerk passport acceptance services at the Carroll County Administration Building in Ossipee. Serves all Carroll County residents.",
      icon: "ri-government-line",
    },
    {
      id: "wlf-3",
      name: "Alton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7 Dan Kelley Dr",
      city: "Alton",
      state: "NH",
      zip: "03809",
      phone: "(603) 875-2771",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Alton — 8 miles south of Wolfeboro",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient option for southern Wolfeboro area and Alton residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "wlf-4",
      name: "Meredith Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "76 Main St",
      city: "Meredith",
      state: "NH",
      zip: "03253",
      phone: "(603) 279-4451",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Meredith — 20 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves western Lakes Region residents. Convenient for Moultonborough and Center Harbor area travelers.",
      icon: "ri-mail-line",
    },
    {
      id: "wlf-5",
      name: "Laconia Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "33 Church St",
      city: "Laconia",
      state: "NH",
      zip: "03246",
      phone: "(603) 524-6271",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "Laconia — 25 miles southwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Larger facility serving the greater Laconia and central Lakes Region. More appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "wlf-6",
      name: "Rochester Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 Allen St",
      city: "Rochester",
      state: "NH",
      zip: "03867",
      phone: "(603) 332-1442",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "Rochester — 30 miles southeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Full-service facility southeast of Wolfeboro. Convenient for Wolfeboro residents heading toward the Seacoast or Boston.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Boston Agency", agency: "120 miles — 2 hour 15 minute drive via NH-28/I-93 plus Boston traffic", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming New Hampshire Has Its Own Passport Agency",
      description: "Wolfeboro and Lakes Region residents often assume Manchester or Concord has a passport agency. Neither does. The closest passport agency is in Boston — 120 miles south. No passport agency exists anywhere in New Hampshire, leaving Lakes Region travelers with no local emergency processing option except registered courier services.",
    },
    {
      icon: "ri-calendar-line",
      title: "Seasonal Residents Not Checking Passports Before Returning to the Lake",
      description: "Wolfeboro has a large population of seasonal residents who split their time between Lake Winnipesaukee and warmer winter locations. Many arrive at the lake in May only to discover expired passports in June when planning summer European travel. Check passport validity before heading north for the season.",
    },
    {
      icon: "ri-global-line",
      title: "Brewster Academy Families Not Planning for Student Visa Timing",
      description: "Brewster Academy families whose students are heading to international summer programs or semester abroad often confuse passport processing with visa requirements. A valid passport must be obtained first, then the visa process begins — which can take additional weeks. Start passport applications 3–4 months before program start dates.",
    },
    {
      icon: "ri-restart-line",
      title: "Not Checking Passport Validity Before Booking From MHT or BOS",
      description: "Lakes Region travelers typically fly from Manchester-Boston Regional (MHT) or Boston Logan (BOS). Many book international flights months ahead without checking passport expiration dates. Most countries require 6 months passport validity beyond your return date. If your passport expires within 6 months of travel, airlines may deny boarding.",
    },
    {
      icon: "ri-image-line",
      title: "Using Lake Vacation Photos as Passport Photos",
      description: "Wolfeboro residents sometimes try to use casual photos or self-taken lake photos for passport applications. Passport photos must be exactly 2x2 inches with a pure white background, neutral expression, and no glasses — only professional passport photo services meet U.S. Department of State standards.",
    },
    {
      icon: "ri-file-list-line",
      title: "Renewing by Mail When In-Person Is Required",
      description: "Wolfeboro residents with passports that expired more than 5 years ago, were issued under age 16, or have a name change without documentation must use Form DS-11 (in-person application) — not DS-82 (mail-in). Filing the wrong form means your application gets rejected and you start over.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Wolfeboro, Laconia, or Rochester." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Boston Passport Agency — 120 miles south via NH-28 and I-93." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Boston Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Wolfeboro or Lakes Region address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Wolfeboro from the Boston passport agency?",
      a: "Wolfeboro is approximately 120 miles from the Boston Passport Agency at the Tip O'Neill Federal Building on Causeway Street — about a 2 hour 15 minute drive south via NH-28 to I-93, plus Boston traffic. However, the Boston agency requires pre-scheduled NPIC appointments and does not accept walk-ins. Appointments often fill 2–3 weeks in advance.",
    },
    {
      q: "Does Wolfeboro, NH have its own passport agency?",
      a: "No. Wolfeboro does not have a passport agency. In fact, no passport agency exists anywhere in New Hampshire. The closest passport agency is the Boston Passport Agency at the Tip O'Neill Federal Building, about 120 miles south. Wolfeboro only has passport acceptance facilities — the Wolfeboro Post Office at 24 Central Ave — which can accept DS-11 applications but cannot process or expedite them.",
    },
    {
      q: "I'm a seasonal Wolfeboro resident — can you help if I'm only here for the summer?",
      a: "Absolutely. Many Wolfeboro residents split their time between Lake Winnipesaukee and warmer locations. Fast Passport Center's courier service works regardless of your permanent address — we can process passports for seasonal residents and ship to your Wolfeboro address or a forwarding address.",
    },
    {
      q: "Where do Wolfeboro travelers typically fly internationally?",
      a: "Wolfeboro travelers typically fly from Manchester-Boston Regional Airport (MHT, about 65 miles south) or Boston Logan International Airport (BOS, about 115 miles south). Both offer domestic and international connections. Portland International Jetport (PWM, about 65 miles east in Maine) is another option for Lakes Region travelers.",
    },
    {
      q: "How fast can I get an emergency passport in Wolfeboro?",
      a: "Emergency processing is typically 24–48 hours for Wolfeboro residents. The Lakes Region's position between the Manchester and Portland shipping corridors makes overnight courier routing to Boston efficient. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Where can I get passport photos in Wolfeboro?",
      a: "Rite Aid at 45 Center St in downtown Wolfeboro offers passport photo services. For more options, Walgreens in Laconia (288 Union Ave) and CVS in Rochester (190 Wakefield St) also provide passport photos. Always verify your photos meet exact 2x2 inch specifications with a white background.",
    },
    {
      q: "Does the Wolfeboro Post Office process passports?",
      a: "Yes. The Wolfeboro Post Office at 24 Central Ave accepts passport applications as a passport acceptance facility. They can accept DS-11 applications and verify your documents. Call (603) 569-3131 to schedule an appointment. Hours are limited in this small-town post office.",
    },
    {
      q: "How do I renew my passport in Wolfeboro?",
      a: "Adult renewals meeting all DS-82 eligibility criteria (16 or older, issued within 15 years, same name) can be done entirely by mail. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than self-service USPS (8–11 weeks). If you're not eligible for mail-in renewal, visit the Wolfeboro Post Office for an in-person DS-11 application.",
    },
    {
      q: "I'm a Brewster Academy parent — how early should we start passport prep for summer programs?",
      a: "Brewster Academy families should start passport preparation 3–4 months before international program start dates. Fast Passport Center processes passports in 2–9 business days, but student visa processing after receiving the passport can take several additional weeks. Do not wait until the school year ends.",
    },
    {
      q: "Can Fast Passport Center help Alton Bay and Tuftonboro residents too?",
      a: "Absolutely. We serve all of the eastern Lakes Region including Wolfeboro, Alton, Alton Bay, Tuftonboro, Ossipee, Wakefield, New Durham, and Moultonborough. You never need to drive to Boston for service — our courier handles everything from document pickup to delivery.",
    },
    {
      q: "Does winter weather affect courier service from Wolfeboro?",
      a: "New Hampshire winters can be challenging, but overnight courier routes from the Lakes Region to Boston via I-93 remain operational year-round. Fast Passport Center monitors weather conditions and adjusts routing as needed. For winter travel, we recommend starting passport applications at least 4–6 weeks before departure.",
    },
    {
      q: "Is the Carroll County Clerk faster than the Wolfeboro Post Office for passport applications?",
      a: "Both the Wolfeboro Post Office and the Carroll County Clerk in Ossipee serve as passport acceptance facilities with similar processing capabilities. The County Clerk's office in Ossipee (10 miles east) often has more appointment availability than the small Wolfeboro Post Office. However, neither facility can expedite processing — that's where Fast Passport Center's courier service comes in.",
    },
  ],
  photoLocations: [
    { name: "Rite Aid — Wolfeboro", address: "45 Center St, Wolfeboro, NH 03894", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Laconia", address: "288 Union Ave, Laconia, NH 03246", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Rochester", address: "190 Wakefield St, Rochester, NH 03867", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walmart Photo — Gilford", address: "1458 Lakeshore Rd, Gilford, NH 03249", hours: "Mon–Sun: 7AM–9PM" },
    { name: "UPS Store — Gilford", address: "1428 Lakeshore Rd, Gilford, NH 03249", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM" },
    { name: "FedEx Office — Manchester", address: "1500 S Willow St, Manchester, NH 03103", hours: "Mon–Fri: 7:30AM–7PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "NH DMV — Ossipee", address: "50 Fox Run Rd, Center Ossipee, NH 03814", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Closest DMV to Wolfeboro. REAL ID, driver's license, and state ID services for Carroll County residents.", featured: true },
    { name: "NH DMV — Laconia", address: "171 Daniel Webster Hwy, Belmont, NH 03220", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Larger DMV serving the central Lakes Region. About 25 miles southwest of Wolfeboro.", featured: false },
    { name: "NH DMV — Rochester", address: "150 Wakefield St, Rochester, NH 03867", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Southeastern NH DMV location. About 30 miles southeast of Wolfeboro.", featured: false },
  ],
  airports: [
    {
      code: "MHT",
      name: "Manchester-Boston Regional Airport",
      distance: "65 miles south of Wolfeboro",
      address: "1 Airport Rd, Manchester, NH 03103",
      note: "New Hampshire's largest airport with domestic routes via major carriers. About 1 hour 15 minutes via NH-28 and I-93. The most convenient departure point for Lakes Region travelers.",
      flightUrl: "https://www.flymanchester.com/",
      flightLabel: "MHT Airport",
      featured: true,
    },
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "65 miles east of Wolfeboro",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport with domestic routes. About 1 hour 15 minutes via NH-25 and ME-25. Convenient for eastern Lakes Region travelers.",
      flightUrl: "https://portlandjetport.org/",
      flightLabel: "PWM Airport",
      featured: false,
    },
    {
      code: "BOS",
      name: "Boston Logan International Airport",
      distance: "115 miles south of Wolfeboro",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "New England's largest international gateway with direct flights worldwide. About 2 hours 15 minutes via NH-28 and I-93. Co-located with the Boston Passport Agency.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "BOS Airport",
      featured: false,
    },
    {
      code: "LEB",
      name: "Lebanon Municipal Airport",
      distance: "65 miles west of Wolfeboro",
      address: "5 Airpark Rd, Lebanon, NH 03766",
      note: "Regional airport serving the Upper Valley. About 1 hour 15 minutes west. Limited commercial service — primarily for connecting flights.",
      flightUrl: "https://flyleb.com/",
      flightLabel: "LEB Airport",
      featured: false,
    },
  ],
};